// src/components/SetupWizard/WizardStep3_StrategicIntent.js
//
// Phase 3, Build Step 4: Strategic Intent / Questionnaire — REAL IMPLEMENTATION
//
// Loads the assigned questionnaire template, embeds PublicQuestionnaire,
// and on final submission calls submitQuestionnaireFromInvite with
// isNativeSubmission=true to trigger the Phase 2 automated pipeline
// (DLP governance → sanitize → GPT-4o synthesis → Firestore batch write).
//
// BACKEND REQUIREMENT: submitQuestionnaireFromInvite Cloud Function must be
// updated with a ~10-line branch to accept { clientId, isNativeSubmission: true }
// as an alternative to { token }. This branch:
//   - Skips token lookup
//   - Uses provided clientId directly
//   - Authenticates via context.auth.uid (logged-in FCFO)
//   - Runs the SAME auto-processing pipeline (format → DLP → synthesize → write)
//   - Creates the documents metadata entry (same as token path)
//
// Props:
//   clientId           — string, the client being set up
//   clientData         — client document from Firestore
//   user               — Firebase auth user object
//   userData           — user document (contains assignedQuestionnaireTemplateId)
//   onStepComplete     — callback() when questionnaire is submitted and setup finalized
//   onBack             — callback to go back to Step 2
//   onClientDataChange — callback to refresh parent data

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Collapse,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Divider
} from '@mui/material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EmailIcon from '@mui/icons-material/Email';
import { getFirestore, doc, getDoc, collection, query, where, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { app, functions } from '../../firebase';
import { toast } from 'react-toastify';
import PublicQuestionnaire from '../PublicQuestionnaire';

const db = getFirestore(app);

const WizardStep3_StrategicIntent = ({
  clientId,
  clientData,
  user,
  userData,
  onStepComplete,
  onBack,
  onClientDataChange
}) => {
  // Template loading
  const [templateData, setTemplateData] = useState(null);
  const [templateLoading, setTemplateLoading] = useState(true);
  const [templateError, setTemplateError] = useState(null);

  // Existing response detection (for resume)
  const [existingResponse, setExistingResponse] = useState(null);
  const [initialResponses, setInitialResponses] = useState(null);
  const [draftCheckComplete, setDraftCheckComplete] = useState(false);

  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionComplete, setSubmissionComplete] = useState(false);
  const [isFinalizingSetup, setIsFinalizingSetup] = useState(false);
  const [progressStage, setProgressStage] = useState(0);

  // Email-to-client modal
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [ceoEmail, setCeoEmail] = useState('');
  const [customMessage, setCustomMessage] = useState('');
    const [isSendingEmail, setIsSendingEmail] = useState(false);

  // Billing confirmation modal (2nd+ client activation)
  const [billingModalOpen, setBillingModalOpen] = useState(false);
  const [billingPreview, setBillingPreview] = useState(null);
  const [billingPreviewLoading, setBillingPreviewLoading] = useState(false);
  const [billingPreviewError, setBillingPreviewError] = useState(null);
  const [awaitingBillingConfirmation, setAwaitingBillingConfirmation] = useState(false);

  // Scroll ref for PublicQuestionnaire
  const scrollContainerRef = useRef(null);

  // Get template ID from user data
  const templateId = userData?.assignedQuestionnaireTemplateId;
  const templateVersion = userData?.assignedQuestionnaireTemplateVersion;

  // Check if questionnaire was already submitted (for resume)
  const questionnaireAlreadySubmitted = !!clientData?.clientStrategicNarrative;

    // ----- Billing Preview Helper -----
  const fetchBillingPreview = async () => {
    setBillingPreviewLoading(true);
    setBillingPreviewError(null);

    try {
      const previewFn = httpsCallable(functions, 'previewClientAddCharge');
      const result = await previewFn({});
      setBillingPreview(result.data);
    } catch (err) {
      console.warn('[WizardStep3] Billing preview failed:', err.message);
      setBillingPreviewError('Could not load charge preview. You can still proceed.');
    } finally {
      setBillingPreviewLoading(false);
    }
  };

  // ----- Load Template -----
  useEffect(() => {
    if (!templateId) {
      setTemplateLoading(false);
      setTemplateError('No questionnaire template assigned. Please contact your administrator.');
      return;
    }

    const loadTemplate = async () => {
      setTemplateLoading(true);
      setTemplateError(null);

      try {
        const templateDoc = await getDoc(doc(db, 'questionnaireTemplates', templateId));
        if (!templateDoc.exists()) {
          throw new Error('Questionnaire template not found.');
        }
        setTemplateData(templateDoc.data());
      } catch (err) {
        console.error('[WizardStep3] Error loading template:', err);
        setTemplateError(err.message || 'Failed to load questionnaire template.');
      } finally {
        setTemplateLoading(false);
      }
    };

    loadTemplate();
  }, [templateId]);

  // ----- Check for Existing Draft/Response -----
  useEffect(() => {
    if (!clientId || !templateId) {
      setDraftCheckComplete(true);
      return;
    }

    const responsesRef = collection(db, 'clients', clientId, 'ceoQuestionnaireResponses');
    const q = query(
      responsesRef,
      where('questionnaireTemplateId', '==', templateId),
      orderBy('lastSavedAt', 'desc'),
      limit(1)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      if (!snapshot.empty) {
        const responseDoc = snapshot.docs[0];
        const responseData = { id: responseDoc.id, ...responseDoc.data() };
        setExistingResponse(responseData);

        // If there's an existing draft, convert responses for PublicQuestionnaire
        if (responseData.status === 'draft' && responseData.responses) {
          let responseObj = {};
          if (Array.isArray(responseData.responses)) {
            // Backend stored as array of {questionId, answer} objects
            responseData.responses.forEach(resp => {
              responseObj[resp.questionId] = resp.answer;
            });
          } else if (typeof responseData.responses === 'object') {
            // Backend stored as object (same format PublicQuestionnaire uses)
            responseObj = responseData.responses;
          }
          setInitialResponses(responseObj);
        }

        // If synthesis is already complete, mark as submitted
        if (responseData.status === 'synthesis_complete' || responseData.status === 'submitted_pii_review_pending') {
          // Don't auto-set submissionComplete here — let the already-submitted check handle it
        }
      }
      setDraftCheckComplete(true);
    }, (err) => {
      console.error('[WizardStep3] Error listening for responses:', err);
      setDraftCheckComplete(true);
    });

    return () => unsubscribe();
  }, [clientId, templateId]);

  // ----- Handle Questionnaire Submission -----
  const handleQuestionnaireSubmit = useCallback(async (responses, isSubmission = true) => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    const isFinalSubmission = isSubmission;

    try {
      // Show staged progress IMMEDIATELY for final submissions (before awaiting pipeline)
      if (isFinalSubmission) {
        setSubmissionComplete(true);
        setProgressStage(1);
        setTimeout(() => setProgressStage(2), 6000);
        setTimeout(() => setProgressStage(3), 18000);
      }

      // Call submitQuestionnaireFromInvite with isNativeSubmission flag
      const submitQuestionnaire = httpsCallable(functions, 'submitQuestionnaireFromInvite');
      const result = await submitQuestionnaire({
        clientId,
        responses,
        isNativeSubmission: true,
        questionnaireTemplateId: templateId,
        templateVersion: templateVersion || templateData?.version,
        isDraft: !isFinalSubmission,
        userId: user?.id || user?.uid,
        userEmail: user?.email
      });

      if (result.data?.error) {
        throw new Error(result.data.error);
      }

            if (isFinalSubmission) {
        // Fresh Firestore read — userData prop may be stale if a prior
        // client was activated during this session
        let liveActiveClientCount = userData?.activeClientCount || 0;
        let liveAccountType = userData?.accountType;
        try {
          const userSnap = await getDoc(doc(db, 'users', user.uid));
          if (userSnap.exists()) {
            liveActiveClientCount = userSnap.data().activeClientCount || 0;
            liveAccountType = userSnap.data().accountType;
          }
        } catch (err) {
          console.warn('[WizardStep3] Fresh user read failed, using prop:', err.message);
        }

        if (liveAccountType === 'Paid' && liveActiveClientCount > 0) {
          setSubmissionComplete(false);
          setAwaitingBillingConfirmation(true);
          setBillingModalOpen(true);
          await fetchBillingPreview();
        } else {
          // First client or non-paid user
          finalizeSetup();
        }
      } else {
        toast.success('Draft saved successfully!');
      }

    } catch (err) {
      console.error('[WizardStep3] Submission error:', err);
      toast.error(`Failed to ${isFinalSubmission ? 'submit' : 'save draft'}: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  }, [clientId, templateId, templateVersion, templateData, user, userData, isSubmitting]);

  // ----- Finalize Setup -----
  const finalizeSetup = async () => {
    setIsFinalizingSetup(true);

    try {
      const markComplete = httpsCallable(functions, 'markUserSetupComplete');

      // Race against a 45-second client-side timeout to prevent deadline-exceeded blocking UX
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('client_timeout')), 45000)
      );

      await Promise.race([
        markComplete({ clientId }),
        timeoutPromise
      ]);

      if (onClientDataChange) {
        onClientDataChange({ silent: true });
      }

      setProgressStage(4);

      setTimeout(() => {
        onStepComplete();
      }, 3500);
    } catch (err) {
      console.error('[WizardStep3] Finalization error (non-fatal):', err.message);

      // Refresh data regardless — the backend likely completed even if the call timed out
      if (onClientDataChange) {
        onClientDataChange({ silent: true });
      }

      setProgressStage(4);

      setTimeout(() => {
        onStepComplete();
      }, 3500);
    } finally {
      setIsFinalizingSetup(false);
    }
  };

  // ----- Email to Client -----
  const handleSendEmail = async () => {
    if (!ceoEmail) {
      toast.error('Please enter the CEO email address.');
      return;
    }

    setIsSendingEmail(true);

    try {
      const sendInvite = httpsCallable(functions, 'sendNativeQuestionnaireInvite');
      const result = await sendInvite({
        clientId,
        questionnaireTemplateId: templateId,
        templateVersion: templateVersion || templateData?.version,
        ceoEmail,
        customMessage
      });

      if (result.data?.success) {
        toast.success('Questionnaire invitation sent to ' + ceoEmail);
        setEmailModalOpen(false);
        setCeoEmail('');
        setCustomMessage('');
      } else {
        throw new Error(result.data?.message || 'Failed to send invitation.');
      }
    } catch (err) {
      console.error('[WizardStep3] Send email error:', err);
      toast.error(`Error: ${err.message}`);
    } finally {
      setIsSendingEmail(false);
    }
  };

  // ----- Submission Complete State (just submitted) -----
  // NOTE: This check MUST come before questionnaireAlreadySubmitted.
  // During submission, the backend writes clientStrategicNarrative mid-pipeline,
  // which flips questionnaireAlreadySubmitted=true via the parent's real-time listener.
  // Without this ordering, the staged progress gets hijacked by the "already submitted" screen.
    if ((submissionComplete && !awaitingBillingConfirmation) || isFinalizingSetup) {
    const stages = [
      { label: 'Submitting responses...', detail: 'Sending your answers to the processing pipeline.' },
      { label: 'Running AI governance scan...', detail: 'Detecting and redacting any sensitive information (PII) before AI analysis.' },
      { label: 'Generating strategic insights...', detail: 'Our AI is synthesizing your responses into a strategic profile for this client.' },
      { label: 'Activating client...', detail: 'Finalizing setup and preparing your client dashboard.' },
      { label: 'Setup complete!', detail: 'Your client is now active and ready.' },
    ];

    const currentStage = stages[progressStage] || stages[0];
    const isComplete = progressStage >= 4;

    return (
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 300,
        gap: 2,
        px: 2
      }}>
        {isComplete ? (
          <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main' }} />
        ) : (
          <CircularProgress size={48} />
        )}

        <Typography variant="h6" textAlign="center">
          {currentStage.label}
        </Typography>

        <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ maxWidth: 420 }}>
          {currentStage.detail}
        </Typography>

        {!isComplete && (
          <Typography variant="caption" color="text.disabled" sx={{ mt: 1 }}>
            This typically takes 30–60 seconds
          </Typography>
        )}
      </Box>
    );
  }

  // ----- Already Submitted State (resume flow) -----
    if (questionnaireAlreadySubmitted && !awaitingBillingConfirmation) {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Strategic Intent
        </Typography>

        <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 3 }}>
          Strategic questionnaire already submitted and processed for this client.
        </Alert>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button onClick={onBack} sx={{ textTransform: 'none' }}>
            ← Back
          </Button>
          <Button
            variant="contained"
            onClick={() => onStepComplete()}
            sx={{ minWidth: 180 }}
          >
            Complete Setup
          </Button>
        </Box>
      </Box>
    );
  }

  // ----- Loading State -----
  if (templateLoading || !draftCheckComplete) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200, gap: 2 }}>
        <CircularProgress />
        <Typography>{templateLoading ? 'Loading questionnaire...' : 'Checking for saved progress...'}</Typography>
      </Box>
    );
  }

  // ----- Error State -----
  if (templateError) {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Strategic Intent
        </Typography>
        <Alert severity="error" sx={{ mb: 3 }}>{templateError}</Alert>
        <Button onClick={onBack} sx={{ textTransform: 'none' }}>
          ← Back
        </Button>
      </Box>
    );
  }

  // ----- Main Questionnaire Form -----
  const mode = existingResponse?.status === 'draft' ? 'continue_draft' : 'fill';

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Strategic Intent
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Answer key strategic questions about your client's business. All questions are
        optional. On submission, responses are processed through our AI governance
        pipeline — PII is automatically detected and redacted before AI analysis.
      </Typography>

      {/* Secondary option: Email to client */}
      <Box sx={{ mb: 2 }}>
        <Button
          size="small"
          startIcon={<EmailIcon />}
          onClick={() => setEmailModalOpen(true)}
          sx={{ textTransform: 'none', color: 'text.secondary', fontSize: '0.85rem' }}
        >
          Or email this questionnaire to your client's CEO
        </Button>
      </Box>

      {existingResponse?.status === 'draft' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You have a saved draft. Your previous answers have been loaded.
        </Alert>
      )}

      {/* Questionnaire form */}
      <Box
        ref={scrollContainerRef}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          mb: 2
        }}
      >
        <PublicQuestionnaire
  template={templateData}
  onSubmitSuccess={handleQuestionnaireSubmit}
  isSubmitting={isSubmitting}
  mode={mode}
  initialResponses={initialResponses}
  user={user}
  clientId={clientId}
  scrollContainerRef={scrollContainerRef}
  clientData={clientData}
/>
      </Box>

      {/* Back button — PublicQuestionnaire has its own Submit/Save buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 1 }}>
        <Button onClick={onBack} sx={{ textTransform: 'none' }}>
          ← Back
        </Button>
      </Box>

      {/* Email-to-Client Modal */}
      <Dialog open={emailModalOpen} onClose={() => setEmailModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Email Questionnaire to Client</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Send a secure link to your client's CEO. They can complete the questionnaire independently, and the wizard will detect their submission automatically.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="CEO Email Address"
            type="email"
            fullWidth
            variant="outlined"
            value={ceoEmail}
            onChange={(e) => setCeoEmail(e.target.value)}
            sx={{ mt: 1 }}
          />
          <TextField
            margin="dense"
            label="Custom Message (Optional)"
            type="text"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={customMessage}
            onChange={(e) => setCustomMessage(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ p: '0 24px 16px' }}>
          <Button onClick={() => setEmailModalOpen(false)} disabled={isSendingEmail}>Cancel</Button>
          <Button onClick={handleSendEmail} variant="contained" disabled={isSendingEmail}>
            {isSendingEmail ? <CircularProgress size={24} /> : 'Send Invite'}
          </Button>
        </DialogActions>
            </Dialog>

      {/* Billing Confirmation Modal (2nd+ client) */}
      <Dialog
        open={billingModalOpen}
        onClose={() => {
          if (!isFinalizingSetup && !billingPreviewLoading) {
            setBillingModalOpen(false);
            setAwaitingBillingConfirmation(false);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight="bold">
            Confirm Additional Client
          </Typography>
        </DialogTitle>

        <DialogContent>
          {billingPreviewLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
              <CircularProgress size={24} />
              <Typography variant="body2" color="text.secondary">
                Loading billing details...
              </Typography>
            </Box>
          ) : billingPreviewError ? (
            <Box>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Adding <strong>{clientData?.name || clientData?.companyName || 'this client'}</strong> will add{' '}
                <strong>${(userData?.costPerClient ?? 89).toFixed(2)}/month</strong> to your subscription.
              </Typography>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Prorated charge preview unavailable. Your card on file will be charged the prorated amount for the remainder of this billing cycle.
              </Alert>
            </Box>
          ) : billingPreview ? (
            <Box>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Adding <strong>{clientData?.name || clientData?.companyName || 'this client'}</strong> will add{' '}
                <strong>${(userData?.costPerClient ?? 89).toFixed(2)}/month</strong> to your subscription.
              </Typography>

              <Box
                sx={{
                  p: 2,
                  bgcolor: 'grey.50',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'grey.200',
                  mb: 2
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Charged today (prorated)
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {billingPreview.amountDue === 0
                      ? 'No charge today (trial active)'
                      : `$${(billingPreview.amountDue / 100).toFixed(2)} ${billingPreview.currency?.toUpperCase()}`}
                  </Typography>
                </Box>

                <Divider sx={{ my: 1 }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    Starting next billing cycle
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    ${(userData?.costPerClient ?? 89).toFixed(2)}/month
                  </Typography>
                </Box>
              </Box>

              {(billingPreview.cardBrand || billingPreview.cardLast4) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <CreditCardIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    Charging{' '}
                    <strong>
                      {billingPreview.cardBrand
                        ? billingPreview.cardBrand.charAt(0).toUpperCase() + billingPreview.cardBrand.slice(1)
                        : ''}
                      {billingPreview.cardLast4 ? ` ••••${billingPreview.cardLast4}` : ''}
                    </strong>
                  </Typography>
                </Box>
              )}
            </Box>
          ) : null}

          <Button
            size="small"
            endIcon={<OpenInNewIcon fontSize="small" />}
            onClick={async () => {
              try {
                const portalFn = httpsCallable(functions, 'createBillingPortalSession');
                const result = await portalFn({});
                window.open(result.data.url, '_blank');
              } catch (err) {
                toast.error('Could not open billing portal.');
              }
            }}
            sx={{ textTransform: 'none', p: 0, fontSize: '0.8rem' }}
          >
            Change payment method
          </Button>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={() => {
              setBillingModalOpen(false);
              setAwaitingBillingConfirmation(false);
            }}
            disabled={isFinalizingSetup}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={() => {
              setBillingModalOpen(false);
              setAwaitingBillingConfirmation(false);
              finalizeSetup();
            }}
            disabled={isFinalizingSetup || billingPreviewLoading}
          >
            {isFinalizingSetup ? <CircularProgress size={20} /> : 'Confirm & Activate Client'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WizardStep3_StrategicIntent;